import { PLAN_FEATURE_INFO, PLAN_FEATURES, PLAN_TIER_INFO, PLAN_TIERS, TRIAL_DAYS, type PlanTier } from "@gf/shared";
import { cx } from "@gf/ui";
import type { ReactNode } from "react";
import { discountedCents, type SignupDiscount } from "@gf/shared/signup";
import { formatUsd } from "../../../lib/billing";

/** What a tier adds over the one below it, in one line. */
function highlights(tier: PlanTier): string {
  const info = PLAN_TIER_INFO[tier];
  const i = PLAN_TIERS.indexOf(tier);
  const below = i > 0 ? PLAN_TIERS[i - 1] : null;
  const adds = PLAN_FEATURES.filter((f) => PLAN_FEATURE_INFO[f].tier === tier).map((f) => PLAN_FEATURE_INFO[f].label);
  const list = adds.length > 1 ? `${adds.slice(0, -1).join(", ")} and ${adds[adds.length - 1]}` : (adds[0] ?? "");
  const accounts = `${info.igAccounts} Instagram account${info.igAccounts === 1 ? "" : "s"}`;
  return below ? `Everything in ${PLAN_TIER_INFO[below].name}, plus ${list}. ${accounts}.` : `The CRM inside Instagram, plus ${list}. ${accounts}.`;
}

/** The three standard plans as radio cards: name, tagline, price, the trial, and what the picked one adds. */
export function PlanPicker({
  value,
  onChange,
  legend,
  disabled,
  trialDays = TRIAL_DAYS,
  discount,
}: {
  value: PlanTier;
  onChange: (plan: PlanTier) => void;
  legend: ReactNode;
  disabled?: boolean;
  trialDays?: number;
  discount?: SignupDiscount | null;
}) {
  return (
    <fieldset className="signup-plans" disabled={disabled}>
      <legend className="signup-plans__legend">{legend}</legend>
      {PLAN_TIERS.map((t) => {
        const info = PLAN_TIER_INFO[t];
        const selected = value === t;
        return (
          <label key={t} className={cx("signup-plan", selected && "signup-plan--selected")}>
            <input className="signup-plan__input" type="radio" name="plan" value={t} checked={selected} onChange={() => onChange(t)} />
            <span className="signup-plan__radio" aria-hidden="true" />
            <span className="signup-plan__body">
              <span className="signup-plan__name">{info.name}</span>
              <span className="signup-plan__tagline">{info.tagline}</span>
            </span>
            <span className="signup-plan__price">
              {discount ? <s className="signup-chosen__was">${info.priceMonthly}/mo</s> : null}
              <span className="signup-plan__amount">
                {discount ? formatUsd(discountedCents(info.priceMonthly * 100, discount)) : `$${info.priceMonthly}`}
                <span className="signup-plan__per">/mo</span>
              </span>
              <span className="signup-plan__trial">{trialDays}-day free trial</span>
              {discount ? <span className="signup-plan__trial">{discount.label}</span> : null}
            </span>
            {selected ? <span className="signup-plan__more">{highlights(t)}</span> : null}
          </label>
        );
      })}
    </fieldset>
  );
}
